import * as React from 'react';

import {
    type CustomizationContentLink,
    type CustomizationFooterGroup,
    type Site,
    SiteInsightsLinkPosition,
} from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    ActionableContainer,
    Actions,
    Button,
    ButtonSkeleton,
    Collection,
    Description,
    DragHandle,
    EmptyState,
    EmptyStateActions,
    ExpandButton,
    ExpandSpacer,
    equalKeys,
    getRandomItemKey,
    Heading,
    InlineInputEdit,
    type Key,
    Tree,
    type TreeData,
    TreeItem,
    TreeItemActions,
    TreeItemContent,
    useDragAndDrop,
    useTreeData,
    useWatchTreeItems,
} from '@gitbook/spine-react';
import { useLiveRef, usePrevious } from '@gitbook/util-react-hooks';
import { assert, filterOutNullable } from '@gitbook/util-ts';

import { SiteInsightsLinkUsageButton } from '../../../SiteInsights';
import { AddSubLinkButton } from '../AddSubLinkButton';
import { CustomizationContentLinkItem } from '../CustomizationContentLinkItem';
import { DeleteTreeItemButton } from '../DeleteTreeItemButton';
import { EditLinkButton } from '../EditLinkButton';
import { InvalidSpaceLinkHoverCard } from '../InvalidSpaceLink';
import { getItemsFactory, isLinkBroken } from '../util';

/** Soft limit for maximum no of footer groups & sub links */
const MAX_NO_OF_GROUPS = 16;
const MAX_NO_OF_SUB_LINKS = 16;

/**
 * Convert tree nodes to footer groups.
 */
function fromTreeNodes(
    treeNodes: TreeData<CustomizationFooterItem>['items']
): CustomizationFooterGroup[] {
    return treeNodes.map(fromTreeNode);
}

/**
 * Convert a tree node to a footer group.
 */
function fromTreeNode(node: TreeData<CustomizationFooterItem>['items'][number]) {
    return {
        title: node.value.title,
        links: (node.children ?? []).map((child) => {
            assert('to' in child.value, 'Expected content link');
            return {
                title: child.value.title,
                to: child.value.to,
            };
        }),
    };
}

/**
 * Hook to handle drag and drop for links.
 */
function useFooterNavDragAndDrop<T extends CustomizationFooterItem>(
    treeRef: React.MutableRefObject<TreeData<T>>
) {
    return useDragAndDrop({
        ...treeRef.current.getDragAndDropOptions(),
        getItems: getItemsFactory(treeRef),
        // We could support copy but it's really a nice to have for this particular use case.
        getAllowedDropOperations: () => ['move'],
        getDropOperation(target, _types, allowedOperations) {
            const tree = treeRef.current;

            // Support only item drops, not root.
            if (target.type !== 'item') {
                return 'cancel';
            }

            // Pick the best allowed operation.
            const bestAllowedOperation = allowedOperations[0];

            // If we drag on the same item, cancel the operation.
            if (tree.draggedKeys.has(target.key)) {
                return 'cancel';
            }

            // If we want to drag just after an expanded item, cancel the operation.
            if (target.dropPosition === 'after' && tree.expandedKeys.has(target.key)) {
                return 'cancel';
            }

            const targetItem = tree.getItem(target.key);

            // We support only one dragged item.
            if (tree.draggedKeys.size !== 1) {
                return 'cancel';
            }

            const draggedKey = Array.from(tree.draggedKeys)[0];
            const draggedItem = tree.getItem(draggedKey);

            // If we can't find the item, cancel the operation.
            if (!targetItem || !draggedItem) {
                return 'cancel';
            }

            // Drag on an item.
            if (target.dropPosition === 'on') {
                // Do not allow to drag on sublinks.
                if (targetItem.parentKey) {
                    return 'cancel';
                }

                // Do not allow to drag group.
                if ('links' in draggedItem.value) {
                    return 'cancel';
                }

                if (!targetItem.children) {
                    return 'cancel';
                }

                // Do not allow to drag if the target item is full.
                if (targetItem.children.length >= MAX_NO_OF_SUB_LINKS) {
                    return 'cancel';
                }

                return bestAllowedOperation;
            }

            // Drag into sub-links.
            if (targetItem.parentKey) {
                // If the item is a group, don't allow it.
                if ('links' in draggedItem.value) {
                    return 'cancel';
                }

                const targetParent = tree.getItem(targetItem.parentKey);

                if (!targetParent?.children) {
                    return 'cancel';
                }

                // If we drag from outside of the parent and it's full, don't allow it.
                if (
                    equalKeys(draggedItem.parentKey, targetItem.parentKey) &&
                    targetParent.children.length >= MAX_NO_OF_SUB_LINKS
                ) {
                    return 'cancel';
                }
            }

            // Drag into groups.
            if (!targetItem.parentKey) {
                // Only allow to drag groups between groups.
                if (!equalKeys(draggedItem.parentKey, targetItem.parentKey)) {
                    return 'cancel';
                }
            }

            // If they are reordered.
            if (equalKeys(draggedItem.parentKey, targetItem.parentKey)) {
                const targetIndex = tree.getItemIndex(target.key);
                const draggedIndex = tree.getItemIndex(draggedKey);
                const resultIndex =
                    target.dropPosition === 'before' ? targetIndex - 1 : targetIndex + 1;

                // Don't allow to reorder if the position will be the same.
                if (resultIndex === draggedIndex) {
                    return 'cancel';
                }
            }

            return bestAllowedOperation;
        },
        onItemDrop: async (event) => {
            const tree = treeRef.current;
            switch (event.dropOperation) {
                case 'move': {
                    if (!event.isInternal) {
                        return;
                    }
                    const keys = (
                        await Promise.all(
                            event.items.map(async (item) => {
                                if (item.kind !== 'text') {
                                    return null;
                                }
                                if (!item.types.has('x-gitbook/item-key')) {
                                    return null;
                                }
                                const text = await item.getText('x-gitbook/item-key');
                                return text;
                            })
                        )
                    ).filter(filterOutNullable);
                    const key = keys[0];
                    if (!key) {
                        return;
                    }
                    const item = tree.getItem(key);
                    const targetItem = tree.getItem(event.target.key);
                    if (!item || !targetItem || !targetItem.children) {
                        return;
                    }
                    tree.move(item.key, targetItem.key, targetItem.children.length);
                    return;
                }
                default:
                    throw new Error(`Unsupported drop operation: ${event.dropOperation}`);
            }
        },
        onMove: (event) => {
            const tree = treeRef.current;
            switch (event.dropOperation) {
                case 'move': {
                    // We support only one dragged item.
                    if (event.keys.size !== 1) {
                        return;
                    }

                    const movedKey = Array.from(event.keys)[0];
                    const movedItem = tree.getItem(movedKey);
                    const targetItem = tree.getItem(event.target.key);
                    if (!targetItem || !movedItem) {
                        return;
                    }
                    const targetParent = tree.getParent(event.target.key);
                    const targetIndex = tree.getItemIndex(event.target.key);
                    if (targetIndex === -1) {
                        return;
                    }

                    const toParentKey = targetParent?.key ?? null;

                    const toIndex =
                        event.target.dropPosition === 'after' && movedItem.parentKey !== toParentKey
                            ? targetIndex + 1
                            : targetIndex;

                    tree.move(movedKey, toParentKey, toIndex);

                    return;
                }
                default:
                    throw new Error(`Unsupported drop operation: ${event.dropOperation}`);
            }
        },
    });
}

type CustomizationFooterItem = CustomizationFooterGroup | CustomizationContentLink;

function checkIsContentLink(
    item: CustomizationFooterGroup | CustomizationContentLink
): item is CustomizationContentLink {
    return 'to' in item;
}

const DEFAULT_GROUP_NAME = 'Untitled';

export function FooterLinksEditor(props: {
    site: Site;
    organizationId: string;
    groups: CustomizationFooterGroup[];
    onChange: (links: CustomizationFooterGroup[]) => void;
    siteSpaceIds: string[];
}) {
    const [initialItems] = React.useState(props.groups);
    const tree = useTreeData<CustomizationFooterItem>({
        initialItems,
        // Generate a random key for each item because we don't have anything stable.
        getKey: getRandomItemKey,
        getChildren: (item) => {
            if ('links' in item && item.links) {
                return item.links;
            }
            return [];
        },
    });
    const treeRef = useLiveRef(tree);

    useWatchTreeItems(tree, (items) => {
        props.onChange(fromTreeNodes(items));
    });

    const { dragAndDropHooks } = useFooterNavDragAndDrop(treeRef);

    const [editStates, setEditStates] = React.useState(
        () => new Map<Key, boolean>(tree.items.map((item) => [item.key, false]))
    );

    const previousItems = usePrevious(tree.items);
    React.useEffect(() => {
        // When a new group is added, edit it.
        if (
            previousItems &&
            previousItems !== tree.items &&
            tree.items.length > previousItems.length
        ) {
            const lastItem = tree.items[tree.items.length - 1];
            setEditStates((prev) => {
                const newState = new Map(prev);
                newState.set(lastItem.key, true);
                return newState;
            });
        }
    }, [previousItems, tree.items]);

    const addGroupButton = (
        <Button
            kind="secondary"
            label="Add section"
            leadingIcon={Icon.Plus}
            disabled={tree.items.length >= MAX_NO_OF_GROUPS}
            tooltip={
                tree.items.length >= MAX_NO_OF_GROUPS
                    ? {
                          label: `You can't add more than ${MAX_NO_OF_GROUPS} sections.`,
                      }
                    : undefined
            }
            onPress={() => {
                tree.append(null, {
                    title: DEFAULT_GROUP_NAME,
                    links: [],
                });
            }}
        />
    );

    const areInteractionsDisabled = Array.from(editStates.values()).some((v) => v);

    return (
        <ActionableContainer
            onKeyDownCapture={(event) => {
                if (areInteractionsDisabled) {
                    event.stopPropagation();
                }
            }}
            onDragStartCapture={(event) => {
                if (areInteractionsDisabled) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            }}
        >
            <Tree
                expandedKeys={tree.expandedKeys}
                onExpandedChange={tree.setExpandedKeys}
                aria-label="Groups"
                items={tree.items}
                dragAndDropHooks={dragAndDropHooks}
                // Disable the opening on press
                onAction={() => {}}
                className="w-full"
                renderEmptyState={() => (
                    <EmptyState>
                        <Heading>No footer links</Heading>
                        <Description>Create a navigation menu by adding sections.</Description>
                        <EmptyStateActions>{addGroupButton}</EmptyStateActions>
                    </EmptyState>
                )}
            >
                {function renderItem(item) {
                    const isBrokenLink = isLinkBroken(item.value, props.siteSpaceIds);
                    return (
                        <TreeItem textValue={item.value.title}>
                            <TreeItemContent>
                                <TreeItemActions>
                                    <DragHandle />
                                    <AddSubLinkButton
                                        treeRef={treeRef}
                                        item={item}
                                        maxSubLinks={MAX_NO_OF_SUB_LINKS}
                                        siteSpaceIds={props.siteSpaceIds}
                                    />
                                    {item.children?.length ? <ExpandButton /> : <ExpandSpacer />}
                                </TreeItemActions>
                                {checkIsContentLink(item.value) ? (
                                    <>
                                        <CustomizationContentLinkItem
                                            contentLink={item.value}
                                            className="flex-1"
                                        />
                                        {isBrokenLink ? <InvalidSpaceLinkHoverCard /> : null}
                                        <TreeItemActions>
                                            {!isBrokenLink ? (
                                                <React.Suspense
                                                    fallback={
                                                        <ButtonSkeleton
                                                            size="small"
                                                            iconOnly
                                                            label="Usage"
                                                        />
                                                    }
                                                >
                                                    <SiteInsightsLinkUsageButton
                                                        size="small"
                                                        iconOnly
                                                        kind="blank"
                                                        link={{
                                                            site: props.site,
                                                            organizationId: props.organizationId,
                                                            contentRef: item.value.to,
                                                            position:
                                                                SiteInsightsLinkPosition.Footer,
                                                        }}
                                                    />
                                                </React.Suspense>
                                            ) : null}
                                            <EditLinkButton
                                                item={item.value}
                                                itemType="link"
                                                onChange={(newItem) => {
                                                    treeRef.current.update(item.key, newItem);
                                                }}
                                                siteSpaceIds={props.siteSpaceIds}
                                            />
                                            <DeleteTreeItemButton
                                                label="Delete link"
                                                treeRef={treeRef}
                                                item={item}
                                            />
                                        </TreeItemActions>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex flex-1">
                                            <EditableTitle
                                                item={item}
                                                treeRef={treeRef}
                                                onEditStateChange={(editing) => {
                                                    setEditStates((prev) => {
                                                        const nextStates = new Map(prev);
                                                        nextStates.set(item.key, editing);
                                                        return nextStates;
                                                    });
                                                }}
                                            />
                                        </div>
                                        <TreeItemActions>
                                            <DeleteTreeItemButton
                                                label="Delete section"
                                                treeRef={treeRef}
                                                item={item}
                                            />
                                        </TreeItemActions>
                                    </>
                                )}
                            </TreeItemContent>
                            {item.children && (
                                <Collection items={item.children}>{renderItem}</Collection>
                            )}
                        </TreeItem>
                    );
                }}
            </Tree>
            {tree.items.length > 0 && <Actions>{addGroupButton}</Actions>}
        </ActionableContainer>
    );
}

function EditableTitle(props: {
    item: TreeData<CustomizationFooterItem>['items'][0];
    treeRef: React.MutableRefObject<TreeData<CustomizationFooterItem>>;
    onEditStateChange: (editing: boolean) => void;
}) {
    const { treeRef, item } = props;
    return (
        <InlineInputEdit
            className="-mx-1 px-1"
            value={item.value.title}
            onSave={(value, { reset }) => {
                const title = value.trim() || DEFAULT_GROUP_NAME;
                if (title === item.value.title) {
                    reset();
                    return;
                }
                treeRef.current.update(item.key, {
                    ...fromTreeNode(item),
                    title,
                });
            }}
            onFocus={() => {
                props.onEditStateChange(true);
            }}
            onBlur={() => {
                props.onEditStateChange(false);
            }}
        />
    );
}
